import { supabase } from '../lib/supabase'
import type { Database } from '../lib/supabase'

type RutApplicant = Database['public']['Tables']['customer_rut_applicants']['Row']
type RutApplicantInsert = Database['public']['Tables']['customer_rut_applicants']['Insert']
type RutApplicantUpdate = Database['public']['Tables']['customer_rut_applicants']['Update']

export type { RutApplicant, RutApplicantInsert, RutApplicantUpdate }

export const rutApplicantsService = {
  // Hämta alla RUT-sökande för en kund (sorterade - första är huvudsökande)
  async getByCustomer(customerId: string): Promise<RutApplicant[]> {
    const { data, error } = await supabase
      .from('customer_rut_applicants')
      .select('*')
      .eq('customer_id', customerId)
      .order('sort_order', { ascending: true })

    if (error) {
      console.error('Error fetching RUT applicants:', error)
      throw error
    }

    return data || []
  },

  // Skapa ny RUT-sökande
  async create(applicant: RutApplicantInsert): Promise<RutApplicant> {
    // Hämta högsta sort_order för denna kund
    const { data: existing } = await supabase
      .from('customer_rut_applicants')
      .select('sort_order')
      .eq('customer_id', applicant.customer_id)
      .order('sort_order', { ascending: false })
      .limit(1)

    const nextSortOrder = existing && existing.length > 0 ? existing[0].sort_order + 1 : 0

    const { data, error } = await supabase
      .from('customer_rut_applicants')
      .insert([{ ...applicant, sort_order: nextSortOrder }])
      .select()
      .single()

    if (error) {
      console.error('Error creating RUT applicant:', error)
      throw error
    }

    return data
  },

  // Uppdatera RUT-sökande
  async update(id: string, updates: RutApplicantUpdate): Promise<RutApplicant> {
    const { data, error } = await supabase
      .from('customer_rut_applicants')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating RUT applicant:', error)
      throw error
    }

    return data
  },

  // Ta bort RUT-sökande
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('customer_rut_applicants')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting RUT applicant:', error)
      throw error
    }
  },

  // Flytta RUT-sökande uppåt (mot huvudsökande)
  async moveUp(customerId: string, applicantId: string): Promise<void> {
    const applicants = await this.getByCustomer(customerId)
    const index = applicants.findIndex(a => a.id === applicantId)

    if (index <= 0) return // Redan högst upp

    // Byt plats med föregående
    const current = applicants[index]
    const previous = applicants[index - 1]

    await Promise.all([
      supabase.from('customer_rut_applicants').update({ sort_order: previous.sort_order }).eq('id', current.id),
      supabase.from('customer_rut_applicants').update({ sort_order: current.sort_order }).eq('id', previous.id)
    ])
  },

  // Flytta RUT-sökande nedåt
  async moveDown(customerId: string, applicantId: string): Promise<void> {
    const applicants = await this.getByCustomer(customerId)
    const index = applicants.findIndex(a => a.id === applicantId)

    if (index < 0 || index >= applicants.length - 1) return // Redan längst ner

    // Byt plats med nästa
    const current = applicants[index]
    const next = applicants[index + 1]

    await Promise.all([
      supabase.from('customer_rut_applicants').update({ sort_order: next.sort_order }).eq('id', current.id),
      supabase.from('customer_rut_applicants').update({ sort_order: current.sort_order }).eq('id', next.id)
    ])
  }
}

// Hjälpfunktion: Maskera personnummer (visa födelsedatum, dölj sista 4)
export function maskPersonalNumber(personalNumber: string): string {
  if (!personalNumber || personalNumber.length < 4) return personalNumber

  // Format: YYYYMMDD-XXXX eller YYMMDD-XXXX
  const dashIndex = personalNumber.indexOf('-')
  if (dashIndex > 0) {
    return personalNumber.substring(0, dashIndex + 1) + '****'
  }

  // Utan bindestreck - maskera sista 4
  return personalNumber.substring(0, personalNumber.length - 4) + '****'
}

// Hjälpfunktion: Validera personnummer (enkel validering)
export function isValidPersonalNumber(personalNumber: string): boolean {
  // Acceptera format: YYYYMMDD-XXXX, YYMMDD-XXXX, YYYYMMDDXXXX, YYMMDDXXXX
  const cleaned = personalNumber.replace(/\D/g, '')
  return cleaned.length === 10 || cleaned.length === 12
}
